import type { CSSProperties, ReactNode } from "react";
import { dinheiro, rotuloDePosicao, rotuloDeResultado, useIdioma, useT } from "../i18n";

const SECTOR_COLOR: Record<string, string> = {
  GOL: "var(--sector-gk)",
  ZAG: "var(--sector-def)",
  LD: "var(--sector-def)",
  LE: "var(--sector-def)",
  VOL: "var(--sector-mid)",
  MC: "var(--sector-mid)",
  MEI: "var(--sector-mid)",
  PD: "var(--sector-att)",
  PE: "var(--sector-att)",
  ATA: "var(--sector-att)",
};

export function strengthColor(value: number): string {
  if (value >= 85) return "var(--strength-star)";
  if (value >= 70) return "var(--strength-high)";
  if (value >= 50) return "var(--strength-mid)";
  return "var(--strength-low)";
}

// ---------------------------------------------------------------------------
// Escudo
// ---------------------------------------------------------------------------

/**
 * Escudo do clube desenhado a partir das duas cores do banco.
 *
 * Os conceitos usam brasões reais, que não existem no banco fictício. Em vez de
 * um retângulo cinza, cada clube ganha um escudo com as próprias cores e a
 * sigla, para a tela ter a mesma leitura visual.
 */
export function Crest({
  colors,
  short,
  size = 40,
  semSigla = false,
}: {
  colors: [string, string];
  short: string;
  size?: number;
  /** Tira a sigla de dentro do escudo, para quem a escreve por fora. */
  semSigla?: boolean;
}): ReactNode {
  const [primary, secondary] = colors;
  return (
    <svg width={size} height={size} viewBox="0 0 40 44" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path
        d="M20 1 38 6v20c0 8-8 14-18 17C10 40 2 34 2 26V6Z"
        fill={primary}
        stroke="rgba(255,255,255,0.28)"
        strokeWidth="1.5"
      />
      <path d="M20 1 38 6v20c0 8-8 14-18 17V1Z" fill={secondary} opacity="0.55" />
      {!semSigla && (
        <text
          x="20"
          y="26"
          textAnchor="middle"
          fontSize="13"
          fontWeight="800"
          fill="#fff"
          style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.45)", strokeWidth: 3 }}
        >
          {short}
        </text>
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Indicadores
// ---------------------------------------------------------------------------

/** Reputação em estrelas, de 1 a 6 níveis mapeados em 5 estrelas. */
export function Stars({ level, max = 5, size = 13 }: { level: number; max?: number; size?: number }) {
  const t = useT();
  const filled = Math.max(1, Math.min(max, Math.round((level / 6) * max)));
  return (
    <span
      className="row"
      style={{ gap: 2 }}
      aria-label={t("comum.estrelas", { marcadas: filled, total: max })}
    >
      {Array.from({ length: max }, (_, i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9Z"
            fill={i < filled ? "var(--accent)" : "transparent"}
            stroke={i < filled ? "var(--accent)" : "var(--text-dim)"}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      ))}
    </span>
  );
}

/** Moral como carinha, igual aos conceitos. */
export function MoraleFace({ value, size = 18 }: { value: number; size?: number }) {
  const t = useT();
  const color = value >= 70 ? "var(--success)" : value >= 45 ? "var(--warning)" : "var(--danger)";
  const mouth =
    value >= 70 ? "M8 14.5c1.3 1.6 2.6 2.4 4 2.4s2.7-.8 4-2.4" : value >= 45 ? "M8 15h8" : "M8 16.9c1.3-1.6 2.6-2.4 4-2.4s2.7.8 4 2.4";
  const rotulo = t("comum.moral", { valor: value });
  return (
    <span title={rotulo} className="row fixo" style={{ gap: 0 }}>
      <svg width={size} height={size} viewBox="0 0 24 24" aria-label={rotulo}>
        <circle cx="12" cy="12" r="9.2" fill="none" stroke={color} strokeWidth="1.8" />
        <circle cx="9" cy="10" r="1.3" fill={color} />
        <circle cx="15" cy="10" r="1.3" fill={color} />
        <path d={mouth} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </span>
  );
}

/**
 * Sigla da posição, na forma de cada idioma.
 *
 * A cor continua saindo da sigla do motor, e não do rótulo: quem traduz a
 * posição não pode mexer no setor a que ela pertence.
 */
export function PositionChip({ pos }: { pos: string }): ReactNode {
  const { idioma } = useIdioma();
  return (
    <span
      className="fixo"
      style={{
        display: "inline-block",
        minWidth: 36,
        textAlign: "center",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.05em",
        color: SECTOR_COLOR[pos] ?? "var(--text-muted)",
        border: `1px solid ${SECTOR_COLOR[pos] ?? "var(--border-strong)"}`,
        borderRadius: 4,
        padding: "2px 5px",
        lineHeight: 1.3,
      }}
    >
      {rotuloDePosicao(pos, idioma)}
    </span>
  );
}

export function StrengthBadge({ value }: { value: number }): ReactNode {
  return (
    <span className="num" style={{ fontWeight: 700, color: strengthColor(value) }}>
      {value}
    </span>
  );
}

/**
 * Moeda do jogo, vermelha quando negativa.
 *
 * O formato mora no i18n, junto com número e data, porque é lá que a etiqueta de
 * local vive. Aqui fica só a cor.
 */
export function Money({ value, strong = false }: { value: number; strong?: boolean }): ReactNode {
  const { idioma } = useIdioma();
  return (
    <span
      className="num"
      style={{ color: value < 0 ? "var(--danger)" : "inherit", fontWeight: strong ? 700 : 400 }}
    >
      {dinheiro(value, idioma)}
    </span>
  );
}

export function Bar({ value, label }: { value: number; label?: string }): ReactNode {
  const color = value >= 70 ? "var(--success)" : value >= 45 ? "var(--warning)" : "var(--danger)";
  return (
    <span className="row" style={{ gap: 6 }} title={label ? `${label}: ${value}` : String(value)}>
      <span
        style={{
          width: 42,
          height: 5,
          borderRadius: 999,
          background: "var(--surface-3)",
          overflow: "hidden",
          display: "inline-block",
        }}
      >
        <span
          style={{
            display: "block",
            width: `${Math.max(0, Math.min(100, value))}%`,
            height: "100%",
            background: color,
          }}
        />
      </span>
      <span className="num muted" style={{ fontSize: 11, minWidth: 24 }}>
        {value}%
      </span>
    </span>
  );
}

const FORM_COLOR: Record<string, string> = {
  V: "var(--success)",
  E: "var(--warning)",
  D: "var(--danger)",
};

/** Últimos jogos, do mais recente para o mais antigo. */
export function FormChips({ form, max = 5 }: { form: string[]; max?: number }): ReactNode {
  const t = useT();
  const { idioma } = useIdioma();
  if (form.length === 0) return <span className="muted">{t("comum.semJogos")}</span>;
  return (
    <span className="row" style={{ gap: 4 }}>
      {/* O motor guarda do mais novo para o mais velho; a tela lê como se
          lê um placar de campanha, da esquerda para a direita, com o último
          jogo na ponta direita. */}
      {form.slice(0, max).reverse().map((result, index) => (
        <span
          key={index}
          style={{
            width: 22,
            height: 20,
            lineHeight: "20px",
            textAlign: "center",
            fontSize: 11,
            fontWeight: 800,
            borderRadius: 4,
            color: "var(--bg-deep)",
            background: FORM_COLOR[result] ?? "var(--text-dim)",
          }}
        >
          {rotuloDeResultado(result, idioma)}
        </span>
      ))}
    </span>
  );
}

/** Anel de pontos, como no painel de desempenho dos conceitos. */
export function PointsRing({
  points,
  played,
  size = 96,
}: {
  points: number;
  played: number;
  size?: number;
}): ReactNode {
  const t = useT();
  const max = Math.max(1, played * 3);
  const ratio = Math.max(0, Math.min(1, points / max));
  const r = size / 2 - 7;
  const circumference = 2 * Math.PI * r;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth="7" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={`${circumference * ratio} ${circumference}`}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span className="num" style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>
          {points}
        </span>
        <span className="label-muted" style={{ marginTop: 2 }}>
          {t("comum.pontos")}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estrutura
// ---------------------------------------------------------------------------

export function Stat({
  label,
  children,
  align = "left",
}: {
  label: string;
  children: ReactNode;
  align?: "left" | "center" | "right";
}): ReactNode {
  return (
    <div style={{ textAlign: align }}>
      <div className="label-muted">{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, marginTop: 3 }}>{children}</div>
    </div>
  );
}

export function Panel({
  title,
  action,
  children,
  padded = true,
  style,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  padded?: boolean;
  style?: CSSProperties;
  className?: string;
}): ReactNode {
  return (
    <section className={className ? `card ${className}` : "card"} style={{ overflow: "hidden", ...style }}>
      {title && (
        <header
          className="row"
          style={{
            justifyContent: "space-between",
            padding: "10px 14px",
            borderBottom: "1px solid var(--border)",
            background: "var(--surface-2)",
          }}
        >
          <h2 className="label">{title}</h2>
          {action}
        </header>
      )}
      <div style={{ padding: padded ? 14 : 0 }}>{children}</div>
    </section>
  );
}
